
import React, { useState, useEffect } from 'react';
import { Sparkles, Heart, Award, Users, Scissors } from 'lucide-react';
import { API_BASE, getImageUrl } from '../config';

export default function About({ t }) {
  const [aboutImage, setAboutImage] = useState('');
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/settings`)
      .then(res => res.json())
      .then(data => setAboutImage(data.about_image || ''))
      .catch(err => console.error("Could not load about image", err));
  }, []);

  useEffect(() => setImageError(false), [aboutImage]);

  const displayImage = !imageError && aboutImage 
    ? getImageUrl(aboutImage) 
    : 'https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';
  
  const displayText = t.about?.text || '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white flex flex-col">
      {/* En-tête élégant - réduit */}
      <div className="relative py-12 px-4 bg-gradient-to-br from-slate-900 via-brand-900 to-slate-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAzLTRzMyAyIDMgNHYyYzAgMi0yIDQtMyA0cy0zLTItMy00di0yem0wLTMwYzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNFY0ek0wIDM0YzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNHYtMnptMC0zMGMwLTIgMi00IDMtNHMzIDIgMyA0djJjMCAyLTIgNC0zIDRzLTMtMi0zLTRWNHoiLz48L2c+PC9nPjwvc3ZnPg==')] animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center p-1 mb-3 bg-white/10 backdrop-blur-xl rounded-full border border-white/30 px-4">
            <Sparkles className="h-3 w-3 mr-2 text-brand-300" />
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-brand-50">{t.about?.badge || 'Notre Histoire'}</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-black text-white mb-2 font-serif italic tracking-tight">
            {t.about?.title || 'À Propos'}
          </h2>
          <p className="text-base text-slate-200 font-medium opacity-90">
            {t.about?.subtitle || 'Passion, Excellence & Tradition'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 flex-1 flex flex-col">
        {/* Section principale avec image et texte - compacte */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200 mb-6 flex-1 flex flex-col lg:flex-row">
          {/* Image avec effet élégant */}
          {(aboutImage || displayImage) && (
            <div className="lg:w-2/5 relative overflow-hidden">
              <div className="relative h-48 lg:h-full group">
                <img 
                  src={displayImage} 
                  alt="About Us" 
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000"
                  onError={() => setImageError(true)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                
                {/* Icône décorative */}
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-xl flex items-center justify-center">
                    <Scissors className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contenu textuel */}
          <div className={`${aboutImage || displayImage ? 'lg:w-3/5' : 'w-full'} p-6 md:p-8 relative flex flex-col justify-center`}>
            <div className="absolute top-4 right-4 opacity-5">
              <Heart className="h-20 w-20 text-slate-900" />
            </div>

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-0.5 bg-brand-600"></div>
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-600">
                  {t.about?.storyTitle || 'Qui sommes-nous ?'}
                </span>
              </div>

              <div className="prose prose-sm max-w-none max-h-64 overflow-auto pr-2"> 
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line font-medium"> 
                  {displayText} 
                </p> 
              </div>
            </div>
          </div>
        </div>

        {/* Section valeurs/caractéristiques - compacte */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-2xl shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-700 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
              <Award className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-base font-black text-slate-900 mb-2 font-serif italic">
              {t.about?.features?.excellence?.title || 'Excellence'}
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              {t.about?.features?.excellence?.desc || "Un savoir-faire d'exception transmis avec passion pour des résultats impeccables."}
            </p>
          </div>

          <div className="bg-white p-5 rounded-2xl shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-700 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
              <Heart className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-base font-black text-slate-900 mb-2 font-serif italic">
              {t.about?.features?.passion?.title || 'Passion'}
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              {t.about?.features?.passion?.desc || "Chaque coupe est réalisée avec amour et dévouement pour sublimer votre beauté."}
            </p>
          </div>

          <div className="bg-white p-5 rounded-2xl shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-700 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
              <Users className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-base font-black text-slate-900 mb-2 font-serif italic">
              {t.about?.features?.proximity?.title || 'Proximité'}
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              {t.about?.features?.proximity?.desc || "Un service personnalisé à domicile pour votre confort et votre bien-être."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
